/**
 * Push Notifications Hook
 * Handles service worker registration and push subscription management
 */
import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if push notifications are supported
const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Convert VAPID key from base64 to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);

  // Check initial state
  useEffect(() => {
    const checkSupport = async () => {
      if (!isPushSupported()) {
        setIsSupported(false);
        setLoading(false);
        return;
      }
      
      setIsSupported(true);
      setPermission(Notification.permission);
      
      try {
        // Register service worker
        const reg = await navigator.serviceWorker.register('/sw.js');
        setRegistration(reg);
        
        // Check if already subscribed
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
        
        // Also check server-side status
        const token = localStorage.getItem('token');
        if (token) {
          const response = await fetch(`${API_URL}/api/push/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setIsSubscribed(data.subscribed);
          }
        }
      } catch (err) {
        console.error('Error checking push status:', err);
        setError(err.message);
      }
      
      setLoading(false);
    };
    
    checkSupport();
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !registration) {
      setError('Push notificaties worden niet ondersteund');
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Notificatie permissie is geweigerd');
        setLoading(false);
        return false;
      }
      
      // Get VAPID public key from server
      const vapidResponse = await fetch(`${API_URL}/api/push/vapid-public-key`);
      if (!vapidResponse.ok) {
        throw new Error('Kon VAPID key niet ophalen');
      }
      const { publicKey } = await vapidResponse.json();
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      
      // Send subscription to server
      const token = localStorage.getItem('token');
      const subscribeResponse = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscription.toJSON())
      });
      
      if (!subscribeResponse.ok) {
        throw new Error('Kon subscription niet opslaan op server');
      }
      
      setIsSubscribed(true);
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('Error subscribing to push:', err);
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, [isSupported, registration]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!registration) {
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get current subscription
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      // Notify server
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setIsSubscribed(false);
      setLoading(false);
      return true;
      
    } catch (err) {
      console.error('Error unsubscribing from push:', err);
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, [registration]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe
  };
};

export default usePushNotifications;
